/**
 * Мобильный таймшит, режим «Сетка-heatmap» («заход 2», дизайн — handoff
 * Direction C, `views-c*.jsx MobileTimesheet`).
 *
 * Зачем рядом с режимом «Список»: список хорош для ввода по одному дню, но не
 * даёт обзора недели. Heatmap — компактная сетка 7×N (день × проект), где
 * насыщенность фона = часы/норма дня: пустая неделя и «дыры» видны сразу.
 * Ввод — тапом по ячейке → bottom-sheet drawer (крупные цели, степпер и чипы
 * вместо мелкого инпута).
 *
 * Все гейты ввода (>8 ч, блок на день отсутствия, удаление при 0, readonly)
 * приходят из общего `useEntryPersist` — те же правила, что в сетке/списке.
 */
import { useState } from "react";
import { Box, Button, Drawer, Group, NumberInput, Stack, Text } from "@mantine/core";
import dayjs from "dayjs";

import { EntryGuardModals, useEntryPersist } from "./entryPersist";
import type { TimesheetEntry } from "../api/client";
import { formatHours } from "../utils/format";
import type { NEUTRALS } from "../theme/tokens";

type Neutrals = (typeof NEUTRALS)["light"];

export interface HeatmapRow {
  projectId: number;
  projectCode: string;
  projectName: string;
  projectColor: string;
  cells: (TimesheetEntry | null)[];
  total: number;
}

interface Props {
  rows: HeatmapRow[];
  /** 7 дат недели (Пн..Вс). */
  dayDates: dayjs.Dayjs[];
  /** Норма часов дня (для интенсивности); 0 — нерабочий. */
  dayNorms: number[];
  /** Индекс сегодняшнего дня в неделе или -1. */
  todayIdx: number;
  /** ISO-даты, покрытые одобренным отсутствием. */
  absenceCoveredDates: Set<string>;
  weekId: number;
  /** Неделя редактируема (статус черновик/возврат). */
  isEditable: boolean;
  neutrals: Neutrals;
  onChanged: () => void;
}

/** Ячейка-заготовка для drawer: какую (день × проект) правим. */
interface DrawerTarget {
  row: HeatmapRow;
  dayIdx: number;
  iso: string;
}

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
/** Быстрые значения часов в drawer. */
const QUICK_HOURS = [0.5, 1, 2, 4, 8];

export function MobileHeatmapGrid({
  rows,
  dayDates,
  dayNorms,
  todayIdx,
  absenceCoveredDates,
  weekId,
  isEditable,
  neutrals,
  onChanged,
}: Props) {
  const [target, setTarget] = useState<DrawerTarget | null>(null);

  return (
    <>
      <Box
        style={{
          border: `1px solid ${neutrals.border}`,
          borderRadius: 8,
          overflow: "hidden",
          background: neutrals.surface,
        }}
        data-testid="mobile-heatmap"
      >
        {/* Шапка: дни недели */}
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: `86px repeat(7, 1fr)`,
            borderBottom: `1px solid ${neutrals.border}`,
            background: neutrals.surfaceAlt,
          }}
        >
          <Box style={{ padding: "6px 8px" }}>
            <Text fz={10} c="dimmed">
              Проект
            </Text>
          </Box>
          {dayDates.map((d, i) => (
            <Box
              key={i}
              style={{
                padding: "6px 0",
                textAlign: "center",
                background: i === todayIdx ? neutrals.todayBg : undefined,
              }}
            >
              <Text
                fz={10}
                fw={i === todayIdx ? 700 : 500}
                c={dayNorms[i] === 0 ? "dimmed" : undefined}
              >
                {DAY_LABELS[i]}
              </Text>
              <Text fz={9} c="dimmed">
                {d.format("DD")}
              </Text>
            </Box>
          ))}
        </Box>

        {/* Строки проектов */}
        {rows.map((row) => (
          <Box
            key={row.projectId}
            style={{
              display: "grid",
              gridTemplateColumns: `86px repeat(7, 1fr)`,
              borderTop: `1px solid ${neutrals.border}`,
              alignItems: "stretch",
            }}
          >
            <Box
              style={{
                padding: "6px 8px",
                display: "flex",
                alignItems: "center",
                gap: 5,
                minWidth: 0,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 3,
                  height: 16,
                  borderRadius: 2,
                  background: row.projectColor,
                  flex: "0 0 auto",
                }}
              />
              <Text fz={10} truncate title={`${row.projectCode} — ${row.projectName}`}>
                {row.projectCode}
              </Text>
            </Box>
            {row.cells.map((cell, dayIdx) => {
              const iso = dayDates[dayIdx].format("YYYY-MM-DD");
              const norm = dayNorms[dayIdx];
              const covered = absenceCoveredDates.has(iso);
              const hours = cell ? parseFloat(cell.hours) : 0;
              // Насыщенность: часы/норма дня (для нерабочих — норма 8 как ориентир).
              const ratio = hours > 0 ? Math.min(hours / (norm > 0 ? norm : 8), 1) : 0;
              const readonly = !isEditable || norm === 0 || covered;
              return (
                <Box
                  key={dayIdx}
                  component="button"
                  type="button"
                  onClick={() => setTarget({ row, dayIdx, iso })}
                  data-testid={`heat-cell-${row.projectId}-${dayIdx}`}
                  aria-label={`${row.projectCode}, ${dayDates[dayIdx].format("DD.MM")}, ${
                    hours > 0 ? `${formatHours(hours)} ч` : "пусто"
                  }`}
                  style={{
                    height: 36,
                    border: "none",
                    borderLeft: `1px solid ${neutrals.border}`,
                    cursor: "pointer",
                    fontFamily: "var(--mantine-font-family-monospace)",
                    fontSize: 11.5,
                    fontVariantNumeric: "tabular-nums",
                    color: ratio > 0.55 ? "#fff" : neutrals.text,
                    background:
                      hours > 0
                        ? `color-mix(in srgb, ${row.projectColor} ${Math.round(
                            25 + ratio * 75,
                          )}%, transparent)`
                        : norm === 0
                          ? neutrals.surfaceAlt
                          : "transparent",
                    opacity: readonly && hours === 0 ? 0.55 : 1,
                  }}
                >
                  {hours > 0 ? formatHours(hours) : ""}
                </Box>
              );
            })}
          </Box>
        ))}

        {/* Σ-строка: итог по дню */}
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: `86px repeat(7, 1fr)`,
            borderTop: `1px solid ${neutrals.border}`,
            background: neutrals.surfaceAlt,
          }}
        >
          <Box style={{ padding: "6px 8px" }}>
            <Text fz={10} fw={600}>
              Итого
            </Text>
          </Box>
          {dayDates.map((_, dayIdx) => {
            const sum = rows.reduce(
              (acc, r) => acc + (r.cells[dayIdx] ? parseFloat(r.cells[dayIdx]!.hours) : 0),
              0,
            );
            const norm = dayNorms[dayIdx];
            return (
              <Box key={dayIdx} style={{ padding: "6px 0", textAlign: "center" }}>
                <Text
                  fz={10.5}
                  fw={600}
                  style={{ fontFamily: "var(--mantine-font-family-monospace)" }}
                  c={sum === 0 ? "dimmed" : norm > 0 && sum < norm ? "orange.7" : undefined}
                >
                  {sum > 0 ? formatHours(sum) : "–"}
                </Text>
              </Box>
            );
          })}
        </Box>
      </Box>

      {target && (
        <CellDrawer
          key={`${target.row.projectId}-${target.iso}`}
          target={target}
          weekId={weekId}
          isEditable={isEditable}
          dayNorm={dayNorms[target.dayIdx]}
          isAbsenceCovered={absenceCoveredDates.has(target.iso)}
          onClose={() => setTarget(null)}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

/** Bottom-sheet редактор одной ячейки: степпер + быстрые чипы. */
function CellDrawer({
  target,
  weekId,
  isEditable,
  dayNorm,
  isAbsenceCovered,
  onClose,
  onChanged,
}: {
  target: DrawerTarget;
  weekId: number;
  isEditable: boolean;
  dayNorm: number;
  isAbsenceCovered: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const entry = target.row.cells[target.dayIdx];
  const {
    value,
    setValue,
    saving,
    validationError,
    pendingConfirm,
    setPendingConfirm,
    blockMsg,
    setBlockMsg,
    commit,
    confirmPending,
  } = useEntryPersist({
    entry,
    weekId,
    projectId: target.row.projectId,
    date: target.iso,
    onChanged,
  });

  const readonlyReason = !isEditable
    ? "Неделя не редактируется в текущем статусе."
    : isAbsenceCovered
      ? "Дата покрыта одобренным отсутствием. Списать часы нельзя."
      : dayNorm === 0
        ? "Нерабочий день по производственному календарю."
        : null;

  const num = typeof value === "string" ? parseFloat(value) : value;

  return (
    <>
      <Drawer
        opened
        onClose={onClose}
        position="bottom"
        size="auto"
        withCloseButton
        title={
          <div>
            <Text fz="sm" fw={600}>
              {target.row.projectCode}
            </Text>
            <Text fz="xs" c="dimmed">
              {target.row.projectName} · {dayjs(target.iso).format("dd, DD.MM")}
            </Text>
          </div>
        }
        data-testid="heat-drawer"
      >
        {readonlyReason ? (
          <Stack gap="xs" pb="md">
            <Text fz="sm" c="dimmed" data-testid="heat-drawer-readonly">
              {readonlyReason}
            </Text>
            {entry && (
              <Text fz="xl" fw={600} style={{ fontFamily: "var(--mantine-font-family-monospace)" }}>
                {formatHours(parseFloat(entry.hours))} ч
              </Text>
            )}
          </Stack>
        ) : (
          <Stack gap="sm" pb="md">
            <Group justify="center" gap="md">
              <Button
                variant="default"
                size="lg"
                aria-label="Убавить 0,5 ч"
                onClick={() => setValue(Math.max(0, (isNaN(num) ? 0 : num) - 0.5))}
              >
                −
              </Button>
              <NumberInput
                value={value}
                onChange={setValue}
                min={0}
                max={24}
                step={0.5}
                decimalScale={2}
                decimalSeparator=","
                inputMode="decimal"
                hideControls
                error={validationError}
                data-testid="heat-drawer-input"
                styles={{
                  input: {
                    width: 96,
                    textAlign: "center",
                    fontSize: 26,
                    height: 52,
                    fontFamily: "var(--mantine-font-family-monospace)",
                  },
                }}
              />
              <Button
                variant="default"
                size="lg"
                aria-label="Прибавить 0,5 ч"
                onClick={() => setValue(Math.min(24, (isNaN(num) ? 0 : num) + 0.5))}
              >
                +
              </Button>
            </Group>

            <Group justify="center" gap="xs">
              {QUICK_HOURS.map((h) => (
                <Button
                  key={h}
                  size="xs"
                  variant="light"
                  onClick={() => setValue(h)}
                  data-testid={`heat-chip-${h}`}
                >
                  {formatHours(h)}
                </Button>
              ))}
            </Group>

            <Group grow mt="xs">
              <Button variant="default" onClick={onClose}>
                Отмена
              </Button>
              <Button
                loading={saving}
                data-testid="heat-drawer-save"
                onClick={async () => {
                  const res = await commit();
                  // На confirm/blocked/invalid НЕ закрываем: иначе размонтируется
                  // модалка подтверждения (>8 ч) или блокировки — вопрос пропадёт.
                  if (res === "saved" || res === "deleted" || res === "noop") onClose();
                }}
              >
                Сохранить
              </Button>
            </Group>
            <Text fz="xs" c="dimmed" ta="center">
              0 или пусто — строка за этот день удалится.
            </Text>
          </Stack>
        )}
      </Drawer>
      <EntryGuardModals
        date={target.iso}
        pendingConfirm={pendingConfirm}
        onCancelConfirm={() => setPendingConfirm(null)}
        onConfirm={async () => {
          // Закрываем drawer только если сервер принял (иначе останется
          // модалка блокировки поверх открытого drawer).
          if (await confirmPending()) onClose();
        }}
        blockMsg={blockMsg}
        onCloseBlock={() => setBlockMsg(null)}
      />
    </>
  );
}
